import { AlertCircle, CheckCircle2, Clock3, Mail, RotateCcw, Send } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, type CommunicationWorkspace, type Conference, type EmailAudiencePreview } from "../../api.js";
import { apiErrorMessage } from "../../../api/client.js";
import { Button } from "../../../design/components/Button.js";
import { Alert, EmptyState, Skeleton } from "../../../design/components/Feedback.js";
import { StatusBadge } from "../../../design/components/StatusBadge.js";
import { StageHeader, StagePanel } from "./StageScaffold.js";

const audienceOptions = [
  ["CORRESPONDING_AUTHORS", "Corresponding authors"],
  ["REVIEWERS", "Assigned reviewers"],
  ["PRESENTERS", "Presenters"],
  ["DELEGATES", "Registered attendees"],
] as const;

function commandKey(prefix: string) {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? Date.now()}`;
}

function readable(value: string) {
  return value.toLowerCase().replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase());
}

function deadlineText(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export function CommunicateStage({ conference }: { conference: Conference }) {
  const [workspace, setWorkspace] = useState<CommunicationWorkspace | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [audience, setAudience] = useState("CORRESPONDING_AUTHORS");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [draftId, setDraftId] = useState("");
  const [preview, setPreview] = useState<EmailAudiencePreview | null>(null);
  const [busy, setBusy] = useState("");
  const testCommand = useRef(commandKey("test-email"));

  const load = useCallback(async () => {
    setError("");
    try { setWorkspace(await api.communicationWorkspace(conference.id, conference.organization_id)); }
    catch (reason) { setError(apiErrorMessage(reason, "Communication operations could not be loaded.")); }
  }, [conference.id, conference.organization_id]);

  useEffect(() => { setWorkspace(null); void load(); }, [load]);

  const invalidatePreview = () => { setDraftId(""); setPreview(null); setNotice(""); };

  const prepareReminder = (deadline: CommunicationWorkspace["deadlines"][number]) => {
    setAudience(deadline.audience_kind);
    setSubject(deadline.suggested_subject);
    setMessage("");
    setDraftId("");
    setPreview(null);
    setNotice("");
    document.getElementById("communication-compose")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const reviewRecipients = async () => {
    if (!subject.trim() || !message.trim()) { setError("Add a subject and message before reviewing recipients."); return; }
    setBusy("preview"); setError(""); setNotice("");
    try {
      const draft = await api.createEmailDraft(conference.id, conference.organization_id, { subject: subject.trim(), body_text: message.trim(), audience: { kind: audience } });
      const result = await api.previewEmailDraft(conference.id, conference.organization_id, draft.id);
      setDraftId(draft.id); setPreview(result);
    } catch (reason) { setError(apiErrorMessage(reason, "The audience preview could not be prepared.")); }
    finally { setBusy(""); }
  };

  const queueMessage = async () => {
    if (!draftId || !preview?.count) return;
    setBusy("queue"); setError("");
    try {
      await api.queueEmailDraft(conference.id, conference.organization_id, draftId, commandKey("message"));
      setNotice("Message queued for secure delivery."); setDraftId(""); setPreview(null); setSubject(""); setMessage("");
      await load();
    } catch (reason) { setError(apiErrorMessage(reason, "The message could not be queued.")); }
    finally { setBusy(""); }
  };

  const retryRecipient = async (draft: CommunicationWorkspace["messages"][number], recipient: { id: string; email: string }) => {
    setBusy(`retry-${recipient.id}`); setError(""); setNotice("");
    try {
      await api.retryEmailRecipient(conference.id, conference.organization_id, draft.id, recipient.id);
      setNotice("Failed delivery returned to the secure queue."); await load();
    } catch (reason) { setError(apiErrorMessage(reason, "The failed delivery could not be retried.")); }
    finally { setBusy(""); }
  };

  const sendTest = async () => {
    setBusy("test"); setError(""); setNotice("");
    try {
      await api.sendTestEmail(conference.id, conference.organization_id, testCommand.current);
      testCommand.current = commandKey("test-email"); setNotice("A delivery test was queued to your verified organizer address.");
    } catch (reason) { setError(apiErrorMessage(reason, "The test email could not be delivered.")); }
    finally { setBusy(""); }
  };

  return <div className="conference-stage communication-center">
    <StageHeader title="Communicate" description="Keep every audience moving with deadline reminders, recipient checks, and visible delivery health." />
    {error ? <Alert tone="danger">{error}</Alert> : null}
    {notice ? <Alert tone="success">{notice}</Alert> : null}
    {!workspace ? <div className="conference-stage-loading"><Skeleton /><Skeleton /><Skeleton /></div> : <>
      <section className="communication-metrics" aria-label="Communication summary">
        <article><Mail aria-hidden="true" /><strong>{workspace.summary.messages}</strong><span>Messages</span></article>
        <article><Clock3 aria-hidden="true" /><strong>{workspace.summary.queued}</strong><span>Queued</span></article>
        <article><CheckCircle2 aria-hidden="true" /><strong>{workspace.summary.delivered}</strong><span>{workspace.summary.delivered} delivered</span></article>
        <article className={workspace.summary.needs_attention ? "needs-attention" : ""}><AlertCircle aria-hidden="true" /><strong>{workspace.summary.needs_attention}</strong><span>Needs attention</span></article>
      </section>

      <StagePanel label="Deadline actions" title="Deadline actions" description="Prepare a correctly targeted reminder from the conference timeline.">
        {workspace.deadlines.length ? <div className="communication-deadlines">{workspace.deadlines.map((deadline) => <article key={deadline.id}>
          <div><StatusBadge tone={deadline.status === "ACTIVE" ? "info" : deadline.status === "UPCOMING" ? "neutral" : "warning"}>{readable(deadline.status)}</StatusBadge><h3>{deadline.title}</h3><p>{deadlineText(deadline.due_at)}</p></div>
          <Button type="button" variant="secondary" aria-label={`Prepare ${deadline.title} reminder`} onClick={() => prepareReminder(deadline)} disabled={deadline.status === "CLOSED"}>Prepare reminder</Button>
        </article>)}</div> : <EmptyState title="No published deadlines" description="Published submission and review phases will create reminder actions here." />}
      </StagePanel>

      <StagePanel label="Compose message" title="Compose message" description="Nothing is sent until Conferis resolves the audience and you confirm the preview.">
        <form id="communication-compose" className="communication-compose" onSubmit={(event) => { event.preventDefault(); void reviewRecipients(); }}>
          <label>Audience<select aria-label="Audience" value={audience} onChange={(event) => { setAudience(event.target.value); invalidatePreview(); }}>{audienceOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Subject<input aria-label="Subject" maxLength={240} value={subject} onChange={(event) => { setSubject(event.target.value); invalidatePreview(); }} required /></label>
          <label className="communication-compose-message">Message<textarea aria-label="Message" rows={7} value={message} onChange={(event) => { setMessage(event.target.value); invalidatePreview(); }} required /></label>
          <aside className="communication-content-preview" aria-label="Message preview"><span>Recipient preview</span><strong>{subject || "Your subject appears here"}</strong><p>{message || "Write a concise operational message. Conferis will send an individualized copy to each permitted recipient."}</p></aside>
          <div className="communication-compose-actions"><Button type="button" variant="ghost" busy={busy === "test"} onClick={() => void sendTest()}>Send test to me</Button><Button type="submit" busy={busy === "preview"}>Review recipients</Button></div>
        </form>
        {preview ? <section className="communication-audience-preview" aria-live="polite"><div><strong>{preview.count} people will receive this message</strong><span>{preview.excluded} excluded by delivery safeguards</span></div>{preview.examples.length ? <ul>{preview.examples.map((example) => <li key={example.email}><span>{example.email}</span><StatusBadge tone={example.state === "RESOLVED" ? "success" : "neutral"}>{readable(example.state)}</StatusBadge></li>)}</ul> : null}<Button type="button" busy={busy === "queue"} disabled={!preview.count} onClick={() => void queueMessage()}><Send size={16} />Queue message</Button></section> : null}
      </StagePanel>

      <StagePanel label="Delivery activity" title="Delivery activity" description="Provider acceptance, verified delivery events, suppressions, and definitive failures stay separate.">
        {workspace.messages.length ? <div className="communication-activity">{workspace.messages.map((draft) => <article key={draft.id}>
          <header><div><StatusBadge tone={draft.state === "QUEUED" ? "info" : "neutral"}>{readable(draft.state)}</StatusBadge><h3>{draft.subject}</h3><p>{readable(draft.audience_kind)} · {deadlineText(draft.created_at)}</p></div><dl><div><dt>Queued</dt><dd>{draft.counts.queued}</dd></div><div><dt>Accepted</dt><dd>{draft.counts.accepted}</dd></div><div><dt>Delivered</dt><dd>{draft.counts.delivered}</dd></div><div><dt>Failed</dt><dd>{draft.counts.failed + draft.counts.reconciliation}</dd></div></dl></header>
          {draft.retryable_recipients.length ? <div className="communication-failures"><strong>Definitive failures</strong>{draft.retryable_recipients.map((recipient) => <div key={recipient.id}><span>{recipient.email}</span><Button type="button" variant="secondary" aria-label={`Retry ${recipient.email}`} busy={busy === `retry-${recipient.id}`} onClick={() => void retryRecipient(draft, recipient)}><RotateCcw size={15} />Retry</Button></div>)}</div> : null}
        </article>)}</div> : <EmptyState title="No saved messages" description="Recipient previews and delivery activity will appear here after the first message is prepared." />}
      </StagePanel>
    </>}
  </div>;
}
